import { useEffect, useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import { IGNORE_REASON_LABEL, IgnoreReason } from '@masamune/shared';
import { IGNORE_REASONS, type IgnoreReasonKey } from './adminApi';

/**
 * Popover "Cestina": motivo (obbligatorio), nota (facoltativa) e la riga che
 * dice cosa succede davvero.
 *
 * Cestinare toglie il documento dal gestionale e basta: su FattureInCloud non
 * si scrive niente e niente viene cancellato dal database. Va detto qui, in una
 * riga, perché "cestina" accanto a una fattura fa pensare a un annullamento
 * fiscale, che è tutta un'altra cosa (serve una nota di credito, la fa il
 * commercialista).
 *
 * Default "Emessa per errore": è il caso più comune. Il motivo resta scritto sul
 * documento e si rilegge nel cestino.
 */
export function TrashPopover({ title, pending, onConfirm, onClose }: {
  /** Cosa si sta cestinando: "2026/57" oppure "5 fatture". */
  title: string;
  pending?: boolean;
  onConfirm: (v: { reason: IgnoreReasonKey; note: string | null }) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<IgnoreReasonKey>(IgnoreReason.ERRORE);
  const [note, setNote] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = () => {
    if (pending) return;
    onConfirm({ reason, note: note.trim() || null });
  };

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      {/* Più largo dei 320px di .adm-pop: "Scartata dal Sistema di Interscambio"
          non deve arrivare tagliato a metà nel select. */}
      <div className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label="Cestina documento"
        style={{ width: 'min(400px, calc(100vw - 32px))' }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}>
        <div className="adm-pop-hd">
          <span className="t-micro">Cestina</span>
          <span className="mono font-semibold truncate">{title}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Motivo</label>
          <select className="inp inp-sm" value={reason} onChange={(e) => setReason(e.target.value as IgnoreReasonKey)} autoFocus>
            {IGNORE_REASONS.map((r) => <option key={r} value={r}>{IGNORE_REASON_LABEL[r]}</option>)}
          </select>
          <label className="field-label">Nota</label>
          <input className="inp inp-sm" placeholder="Facoltativa" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="px-4 pb-3 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          Sparisce da qui. Su FattureInCloud resta: se va annullata davvero serve una nota di credito.
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm" disabled={pending} onClick={submit}>
            <Icon name="trash" size={10} /> Cestina
          </button>
        </div>
      </div>
    </>
  );
}
